/* Coproom, accueil V2 (18/10/2026).
   Deux éléments sans dépendance : <cpx-rail> (rangées produits, flèches) et
   <cpx-hero> (diaporama d'ouverture, onglets avec barre de progression).
   Le fichier peut être inclus par plusieurs sections : la définition n'est faite qu'une fois. */
(function () {
  'use strict';
  if (!('customElements' in window) || customElements.get('cpx-rail')) return;

  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var behavior = function () { return reduceMotion.matches ? 'auto' : 'smooth'; };

  /* ── Rangée produits : flèches précédent / suivant ─────────────── */
  class CpxRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-cpx-track]');
      if (!this.track) return;
      this.prev = this.querySelector('[data-cpx-dir="-1"]');
      this.next = this.querySelector('[data-cpx-dir="1"]');
      this._onClick = this.onClick.bind(this);
      this._update = this.update.bind(this);
      this.addEventListener('click', this._onClick);
      this.track.addEventListener('scroll', this._update, { passive: true });
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(this._update);
        this._ro.observe(this.track);
      } else {
        window.addEventListener('resize', this._update);
      }
      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      if (this.track) this.track.removeEventListener('scroll', this._update);
      if (this._ro) this._ro.disconnect();
      else window.removeEventListener('resize', this._update);
    }

    onClick(event) {
      var button = event.target.closest('[data-cpx-dir]');
      if (!button || !this.contains(button)) return;
      this.step(parseInt(button.getAttribute('data-cpx-dir'), 10) || 1);
    }

    step(direction) {
      var item = this.track.querySelector('[data-cpx-item]');
      var styles = getComputedStyle(this.track);
      var gap = parseFloat(styles.columnGap) || 0;
      var width = item ? item.getBoundingClientRect().width : this.track.clientWidth / 4;
      var visible = this.track.clientWidth - (parseFloat(styles.paddingLeft) || 0) - (parseFloat(styles.paddingRight) || 0);
      var perPage = Math.max(1, Math.floor((visible + gap) / (width + gap)));
      this.track.scrollBy({ left: direction * perPage * (width + gap), behavior: behavior() });
    }

    update() {
      var max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
      if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      this.toggleAttribute('data-static', max <= 0);
    }
  }


  /* ── Diaporama d'ouverture ─────────────────────────────────────────
     Défilement natif (doigt, trackpad), un onglet par diapositive, posé dans le bas du visuel.
     Lecture automatique : la fin de l'animation de la barre de l'onglet actif fait
     passer à la suivante ; la mettre en pause (survol des onglets, onglet du navigateur
     caché) met donc le minuteur en pause. Seul un geste sur le diaporama lui-même
     (glisser sur le côté, onglet, flèche, clavier) arrête la lecture. */
  class CpxHero extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-cpx-slides]');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('[data-cpx-slide]'));
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[data-cpx-dot]'));
      this.index = 0;
      if (!this.track || this.slides.length < 2) return;

      var self = this;
      this._onScroll = function () {
        self.paint();
        window.clearTimeout(self._scrollTimer);
        self._scrollTimer = window.setTimeout(function () {
          var width = self.track.clientWidth || 1;
          self.setActive(Math.round(self.track.scrollLeft / width));
        }, 80);
      };
      this.track.addEventListener('scroll', this._onScroll, { passive: true });

      this.tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () {
          self.stop();
          self.go(i);
        });
        /* Seule la fin de la barre de progression fait avancer (19/10/2026) : une autre
           animation sur l'onglet (ex. « pulse » du thème au survol) ne doit rien déclencher */
        tab.addEventListener('animationend', function (event) {
          if (event.animationName !== 'cpx-hero-progress') return;
          if (self.hasAttribute('data-playing') && i === self.index) self.go((self.index + 1) % self.slides.length);
        });
      });

      /* Arrêt de la lecture pour de bon : seulement un geste VERS le diaporama (27/10/2026).
         Avant, toucher le visuel ou tourner la molette dessus l'arrêtait : sur téléphone, le
         doigt qui fait descendre la page part presque toujours du visuel (plein écran), et la
         lecture s'arrêtait dès le premier défilement. Désormais : glissement horizontal au
         doigt ou au trackpad, flèches du clavier ; le défilement vertical de la page ne compte pas. */
      this._stopOnce = function (event) {
        if (event.type === 'keydown' && event.key === 'Tab') return;
        self.stop();
      };
      this.track.addEventListener('keydown', this._stopOnce);
      this.track.addEventListener('touchstart', function (event) {
        var t = event.touches[0];
        self._touch = t ? { x: t.clientX, y: t.clientY, done: false } : null;
      }, { passive: true });
      this.track.addEventListener('touchmove', function (event) {
        var start = self._touch;
        var t = event.touches[0];
        if (!start || start.done || !t) return;
        var dx = Math.abs(t.clientX - start.x);
        var dy = Math.abs(t.clientY - start.y);
        if (dx < 10 && dy < 10) return;
        start.done = true;
        if (dx > dy) self.stop();
      }, { passive: true });
      this.track.addEventListener('wheel', function (event) {
        if (Math.abs(event.deltaX) > 4 && Math.abs(event.deltaX) > Math.abs(event.deltaY)) self.stop();
      }, { passive: true });
      this._pause = function () { self.toggleAttribute('data-paused', true); };
      this._resume = function () { if (!document.hidden) self.removeAttribute('data-paused'); };
      /* Pause au survol de la souris sur les onglets et les flèches seulement (27/10/2026) :
         le visuel occupe tout le haut de l'écran, la souris y reste posée et la lecture
         restait figée à mi-barre. Sur téléphone, aucun survol (pointerType « touch »). */
      Array.prototype.forEach.call(this.querySelectorAll('.cpx-hero__tabs, [data-cpx-hero-dir]'), function (zone) {
        zone.addEventListener('pointerenter', function (event) { if (event.pointerType === 'mouse') self._pause(); });
        zone.addEventListener('pointerleave', function (event) { if (event.pointerType === 'mouse') self._resume(); });
      });
      /* Clic sur une bannière (lien, bouton ou photo) : mesuré pour Google Analytics */
      this.slides.forEach(function (slide, i) {
        slide.addEventListener('click', function (event) {
          if (event.target.closest('a')) self.report('coproom_hero_click', i);
        });
      });
      /* Flèches précédente / suivante (24/10/2026) : montrent qu'il y a plusieurs bannières */
      Array.prototype.forEach.call(this.querySelectorAll('[data-cpx-hero-dir]'), function (btn) {
        btn.addEventListener('click', function () {
          var dir = parseInt(btn.getAttribute('data-cpx-hero-dir'), 10) || 1;
          var n = self.slides.length;
          self.stop();
          self.go((self.index + dir + n) % n);
        });
      });
      this.addEventListener('focusin', this._pause);
      this.addEventListener('focusout', this._resume);
      this._visibility = function () { self.toggleAttribute('data-paused', document.hidden); };
      document.addEventListener('visibilitychange', this._visibility);

      /* Éditeur de thème : afficher la diapositive sélectionnée */
      this._onBlockSelect = function (event) {
        var target = self.querySelector('[data-cpx-block="' + event.detail.blockId + '"]');
        var i = self.slides.indexOf(target);
        if (i > -1) { self.stop(); self.go(i); }
      };
      document.addEventListener('shopify:block:select', this._onBlockSelect);

      /* Les photos des diapositives suivantes sont en chargement différé : on les
         demande une fois la page chargée, pour qu'elles soient prêtes à l'affichage */
      var warm = function () {
        self.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
          if (img.offsetParent !== null) img.loading = 'eager';
        });
      };
      if (document.readyState === 'complete') window.setTimeout(warm, 1200);
      else window.addEventListener('load', function () { window.setTimeout(warm, 1200); }, { once: true });

      var seconds = parseFloat(this.getAttribute('data-autoplay') || '0');
      var designMode = window.Shopify && window.Shopify.designMode;
      if (seconds > 0 && !reduceMotion.matches && !designMode) {
        this.toggleAttribute('data-playing', true);
      }
      this.setActive(0);
      this.paint();
    }

    disconnectedCallback() {
      this.stop();
      if (this.track) this.track.removeEventListener('scroll', this._onScroll);
      document.removeEventListener('visibilitychange', this._visibility);
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
    }

    stop() {
      this.removeAttribute('data-playing');
      this.removeAttribute('data-paused');
    }

    go(i) {
      var slide = this.slides[i];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: behavior() });
      this.setActive(i);
    }

    /* Mesure des bannières (24/10/2026) : chaque bannière vue et chaque clic sont publiés vers
       les pixels Shopify (pixel « Coproom bannières » → Google Analytics view_promotion /
       select_promotion). Sans pixel, ces appels ne font rien. */
    promo(i) {
      var slide = this.slides[i];
      if (!slide) return null;
      var title = slide.querySelector('.cpx-hero__title');
      return {
        promotion_id: slide.getAttribute('data-cpx-block') || ('slide-' + (i + 1)),
        promotion_name: title ? title.textContent.trim() : ('Bannière ' + (i + 1)),
        creative_slot: 'accueil-' + (i + 1)
      };
    }

    report(name, i, retry) {
      var self = this;
      try {
        var data = this.promo(i);
        if (!data) return;
        if (!(window.Shopify && Shopify.analytics && Shopify.analytics.publish)) {
          /* Les pixels Shopify se chargent après la page : on réessaie une fois */
          if (!retry) window.setTimeout(function () { self.report(name, i, true); }, 2500);
          return;
        }
        Shopify.analytics.publish(name, data);
      } catch (e) {}
    }

    setActive(i) {
      if (i < 0 || i >= this.slides.length || (i === this.index && this._ready)) return;
      this._ready = true;
      this.index = i;
      this._seen = this._seen || {};
      if (!this._seen[i]) { this._seen[i] = true; this.report('coproom_hero_view', i); }
      this.tabs.forEach(function (tab, j) {
        tab.classList.toggle('is-active', j === i);
        tab.setAttribute('aria-current', j === i ? 'true' : 'false');
      });
      this.slides.forEach(function (slide, j) {
        if (j === i) slide.removeAttribute('inert');
        else slide.setAttribute('inert', '');
      });
    }

    /* La navigation (posée sur le visuel) prend la couleur du texte de la diapositive qui
       occupe le plus de place à l'écran : pendant un glissement, elle bascule à mi-chemin
       (19/10/2026). Appelée à chaque défilement : n'écrit l'attribut que s'il change. */
    paint() {
      var width = this.track.clientWidth || 1;
      var i = Math.min(this.slides.length - 1, Math.max(0, Math.round(this.track.scrollLeft / width)));
      var surface = this.slides[i].getAttribute('data-cpx-surface') || 'light';
      if (this.getAttribute('data-surface') !== surface) this.setAttribute('data-surface', surface);
    }
  }

  customElements.define('cpx-rail', CpxRail);
  customElements.define('cpx-hero', CpxHero);
})();
